<vs-card theme="default" *vsLet="meta$ | async as meta" class="block @container">
  <vs-card-header
    class="[&>div]:!flex-wrap [&>div>div:first-child]:!min-w-[200px] [&>div>div:first-child>div]:!flex-nowrap !py-3 @xl:!py-4 !px-4 @xl:!px-5"
    [config]="{
      title: 'Unfunded Warranties',
      subtitle: 'Warranties provisioned by this seller that are pending payment.'
    }"
  >
    <vs-badge *vsLet="(meta?.totalItems ?? null) as totalItems" slot="after-title">
      {{ totalItems === null ? 'loading' : (totalItems | number) + (totalItems === 1 ? ' warranty' : ' warranties') }}
    </vs-badge>

    <!-- Direct children: vs-card-header projects on [slot=end], which only matches direct children. -->
    <!-- Drafts only: the cancel handler rejects a batch that already has a payment. -->
    <button
      *ngIf="isDraftBatch$ | async"
      vs-button
      size="md"
      slot="end"
      appearance="outline"
      class="mr-3"
      [disabled]="isSubmitting"
      (click)="cancelBatch()"
    >
      Change Selection
    </button>

    <button
      *ngIf="openBatch$ | async"
      vs-button
      size="md"
      slot="end"
      theme="primary"
      class="!w-full"
      [disabled]="isSubmitting"
      (click)="openPaymentDialog()"
    >
      <vs-icon class="hidden sm:inline-block" name="credit-card-02"></vs-icon>
      Continue with Payment
    </button>

    <button
      *ngIf="(openBatch$ | async) === null"
      vs-button
      size="md"
      slot="end"
      theme="primary"
      class="!w-full"
      [disabled]="!(selection$ | async)?.count || isSubmitting"
      (click)="proceedToPayment()"
    >
      <vs-icon class="hidden sm:inline-block" name="credit-card-02"></vs-icon>
      Proceed to Payment
    </button>
  </vs-card-header>

  <ws-pay-batch-dialog
    *vsLet="selection$ | async as selection"
    [batchPaymentId]="(openBatch$ | async)?.id ?? null"
    [warrantyCount]="selection?.count ?? 0"
    [amount]="selection?.amount ?? 0"
    [defaultBillingAddress]="distributorAddress"
    (closed)="onPaymentDialogClosed($event)"
  ></ws-pay-batch-dialog>

  <vs-card-content>
    <div
      *ngIf="error$ | async"
      class="flex flex-row gap-2 bg-danger-50 bg-opacity-50 border border-danger-300 rounded-lg p-3 mb-4"
    >
      <vs-icon name="alert-triangle" size="16" class="text-danger-700 shrink-0"></vs-icon>
      <div class="flex flex-col text-danger-700 text-sm">
        <p class="font-semibold">Unable to load unfunded warranties</p>
        <p>Something went wrong fetching this seller's pending warranties. Please try again.</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex justify-between gap-4 mb-4" *vsLet="filters$ | async as filters">
      <div class="flex gap-4 flex-wrap">
        <vs-select
          vsInput
          class="min-w-[212px]"
          [value]="filters?.policyId"
          [config]="{ placeholder: 'All policies', allowDeselect: true }"
          (valueChanges)="onPolicyChange($event)"
        >
          <li *ngFor="let policy of policies" vs-select-option [value]="policy.id">{{ policy.title }}</li>
        </vs-select>

        <vs-select
          vsInput
          class="min-w-[212px]"
          [value]="filters?.planId"
          [disabled]="!filters?.policyId"
          [config]="{ placeholder: filters?.policyId ? 'All plans' : 'Select a policy first', allowDeselect: true }"
          (valueChanges)="onPlanChange($event)"
        >
          <li *ngFor="let plan of plans$ | async" vs-select-option [value]="plan.id">{{ plan.title }}</li>
        </vs-select>

        <button
          type="button"
          class="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white min-w-[240px] hover:border-gray-400 transition-colors"
          [vsMenuTriggerFor]="dateRangeMenu"
        >
          <vs-icon name="calendar" class="text-gray-400" size="16"></vs-icon>
          <div class="flex-1 text-left text-sm">
            <ng-container *ngIf="filters?.startDate || filters?.endDate; else noDateRange">
              <span class="text-gray-900">
                {{ filters?.startDate ? (filters?.startDate | date: 'MMM d') : 'Start' }} - {{ filters?.endDate ?
                (filters?.endDate | date: 'MMM d, y') : 'End' }}
              </span>
            </ng-container>
            <ng-template #noDateRange>
              <span class="text-gray-500">Date provisioned</span>
            </ng-template>
          </div>
          <vs-icon name="chevron-down" class="text-gray-400" size="14"></vs-icon>
        </button>

        <ng-template #dateRangeMenu>
          <vs-menu class="!z-50">
            <div class="p-4">
              <vs-date-picker [range]="true" (selectionChanged)="onDateRangeChange($event)"></vs-date-picker>
            </div>
          </vs-menu>
        </ng-template>
      </div>

      <button *ngIf="hasFilters$ | async" vs-button appearance="clear" size="md" (click)="clearFilters()">
        <vs-icon name="x-close" size="16"></vs-icon>
        Clear
      </button>
    </div>

    <!-- Skeleton Loader (show while initial data is loading) -->
    <div *ngIf="isInitialLoading" class="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      <vs-table-skeleton
        [rows]="(paginationConfig$ | async)?.itemsPerPage ?? itemsPerPage"
        [columns]="8"
        [columnDefs]="[
          { fitContent: true },
          {},
          {},
          {},
          {},
          { fitContent: true },
          { fitContent: true },
          { fitContent: true }
        ]"
        [showHeader]="true"
        [banding]="true"
      ></vs-table-skeleton>
    </div>

    <!-- Content (show after loading completes) -->
    <ng-container *ngIf="!isInitialLoading && (error$ | async) === false">
      <ng-container *ngIf="meta?.totalItems; else noWarranties">
        <ng-container *vsLet="warranties$ | async as warranties">
          <!-- Selection bar — vs-table renders headers as plain text, so Select All lives here. -->
          <div
            class="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 mb-4"
            *vsLet="selection$ | async as selection"
          >
            <ng-container *vsLet="openBatch$ | async as batch">
              <label
                class="flex items-center gap-2.5 text-sm font-medium text-gray-700"
                [class.cursor-pointer]="!batch"
                *vsLet="pageSelection$ | async as page"
              >
                <input
                  type="checkbox"
                  vsInput
                  class="theme-primary"
                  [checked]="page?.isAllSelected ?? false"
                  [indeterminate]="page?.isSomeSelected ?? false"
                  [disabled]="!page?.hasPayableRows || !!batch"
                  (change)="toggleAllOnPage(warranties)"
                  aria-label="Select all warranties on this page"
                />
                Select all on this page
              </label>

              <div class="flex items-center gap-4">
                <p class="text-sm text-gray-600" *ngIf="selection?.count">
                  {{ selection?.count }} selected · Total due:
                  <span class="ml-1 text-base font-semibold text-gray-900">{{ selection?.total }}</span>
                </p>

                <button
                  *ngIf="selection?.count && !batch"
                  vs-button
                  appearance="clear"
                  size="md"
                  [disabled]="isSubmitting"
                  (click)="clearSelection()"
                >
                  Clear selection
                </button>
              </div>
            </ng-container>
          </div>

          <div
            *ngIf="isDraftBatch$ | async"
            class="flex flex-row gap-2 bg-info-50 bg-opacity-50 border border-info-300 rounded-lg p-3 mb-4"
          >
            <vs-icon name="info-circle" size="16" class="text-info-700 shrink-0"></vs-icon>
            <div class="flex flex-col text-info-700 text-sm">
              <p class="font-semibold">Your warranties are reserved for payment, so the selection is locked</p>
              <p>
                It releases these warranties after an hour if it is not paid. Use Change Selection to pick different
                warranties.
              </p>
            </div>
          </div>

          <!-- A submitted batch has no expiry and cannot be cancelled here — only finished or retried. -->
          <div
            *ngIf="isSubmittedBatch$ | async"
            class="flex flex-row gap-2 bg-warn-50 bg-opacity-50 border border-warn-300 rounded-lg p-3 mb-4"
          >
            <vs-icon name="credit-card-02" size="16" class="text-warn-700 shrink-0"></vs-icon>
            <div class="flex flex-col text-warn-700 text-sm">
              <p class="font-semibold">A payment is in progress for these warranties</p>
              <p>
                They stay reserved until it completes and will not be released. Use Continue with Payment to finish it,
                or to try again with another payment method.
              </p>
            </div>
          </div>

          <div
            *ngIf="submitError$ | async as submitError"
            class="flex flex-row gap-2 bg-danger-50 bg-opacity-50 border border-danger-300 rounded-lg p-3 mb-4"
          >
            <vs-icon name="alert-triangle" size="16" class="text-danger-700 shrink-0"></vs-icon>
            <p class="text-danger-700 text-sm">{{ submitError }}</p>
          </div>

          <div
            *ngIf="limitReached$ | async"
            class="flex flex-row gap-2 bg-warn-50 bg-opacity-50 border border-warn-300 rounded-lg p-3 mb-4"
          >
            <vs-icon name="alert-triangle" size="16" class="text-warn-700 shrink-0"></vs-icon>
            <p class="text-warn-700 text-sm">
              A batch payment can hold at most {{ maxBatchSize }} warranties. Pay the selected ones first, then start
              another batch.
            </p>
          </div>

          <div class="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            <vs-table [columns]="columns" [data]="warranties" (sortChanged)="onSortChanged($event)"></vs-table>
            <vs-pagination
              [config]="paginationConfig$ | async"
              (pagination)="pagination$.next({ page: $event.currentPage, limit: $event.itemsPerPage })"
            ></vs-pagination>
          </div>
        </ng-container>
      </ng-container>

      <ng-template #noWarranties>
        <div
          class="flex justify-center border rounded-2xl p-8 bg-base-white shadow"
          *vsLet="hasFilters$ | async as hasFilters"
        >
          <vs-empty-state
            [config]="{
              title: hasFilters ? 'No warranties match these filters' : 'No unfunded warranties',
              subtitle: hasFilters
                ? 'Adjust the filters to view this seller\'s pending warranties.'
                : 'Every warranty sold by this seller has been funded. Warranties awaiting payment will appear here once they are provisioned.',
              imgsrc: 'empty-clipboard'
            }"
          ></vs-empty-state>
        </div>
      </ng-template>
    </ng-container>
  </vs-card-content>
</vs-card>
